import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Card, CardContent, Typography, Box } from '@mui/material';

import { BFF_BASE_URL } from '../auth/authApi';
import { authFetch } from '../auth/http';
import { RootState, AppDispatch } from '../redux/store';
import { selectUser, fetchVectorSpaceStats } from '../vectorSpace/redux/vectorSpaceSlice';
import { Section } from '../shared/components/Section';
import { VectorSpace3D, VectorSpace3DHandle } from './VectorSpace3D';
import { VectorSpaceControls } from './VectorSpaceControls';
import { TOPIC_NAMES } from '../vectorSpace/topics';
import { useSelectedUser } from '../users/hooks/useSelectedUser';
import { GlowAlert } from '../shared/components/GlowAlert';

interface Vector3DPoint {
  id: string;
  x: number;
  y: number;
  z: number;
  topic: string;
  document: string;
  userId?: string;
}

const VectorSpacePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { selectedUserId, loading, error, stats } = useSelector((state: RootState) => state.vectorSpace);
  const users = useSelector((state: RootState) => state.users.users);
  const [sharedUserId, setSharedUserId] = useSelectedUser();
  const themeMode = useSelector((state: RootState) => state.preferences?.theme) || 'dark';
  const resolveTheme = (mode: string): 'dark' | 'light' => {
    if (mode === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    if (mode === 'daytime') {
      const hour = new Date().getHours();
      return hour >= 7 && hour < 19 ? 'light' : 'dark';
    }
    return (mode as 'dark' | 'light') || 'dark';
  };
  const theme = resolveTheme(themeMode);
  const [vector3DPoints, setVector3DPoints] = useState<Vector3DPoint[]>([]);
  const [showLinks, setShowLinks] = useState(false);
  const [loading3D, setLoading3D] = useState(false);
  const [visibleTopics, setVisibleTopics] = useState<Set<string>>(() => new Set(TOPIC_NAMES));
  const vectorSpace3DRef = useRef<VectorSpace3DHandle>(null);

  const handleToggleTopic = (topic: string) => {
    setVisibleTopics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(topic)) {
        newSet.delete(topic);
      } else {
        newSet.add(topic);
      }
      return newSet;
    });
  };

  const handleRefresh = () => {
    fetchVector3D();
  };

  const handleResetCamera = () => {
    vectorSpace3DRef.current?.resetCamera();
  };

  const handleCenterView = () => {
    vectorSpace3DRef.current?.centerView();
  };

  const handleUserChange = (userId: string | null) => {
    dispatch(selectUser(userId));
    setSharedUserId(userId ?? '');
  };

  // Open on the user chosen elsewhere in the admin.
  useEffect(() => {
    dispatch(selectUser(sharedUserId || null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVector3D = async () => {
    setLoading3D(true);
    try {
      const response = await authFetch(`${BFF_BASE_URL}/admin/vectors/3d`);
      if (response.ok) {
        const data = await response.json();
        console.log('Full API response:', data);
        console.log('Response type:', typeof data);
        console.log('Response keys:', Object.keys(data));

        // Try different possible data formats
        let points: unknown[] = [];
        if (data.points && Array.isArray(data.points)) {
          points = data.points;
        } else if (data.data && Array.isArray(data.data)) {
          points = data.data;
        } else if (Array.isArray(data)) {
          points = data;
        }

        console.log(`Loaded ${points.length} vectors`);
        setVector3DPoints(points as Vector3DPoint[]);
      } else {
        const text = await response.text();
        console.error(`API returned status ${response.status}:`, text);
        setVector3DPoints([]);
      }
    } catch (err) {
      console.error('Failed to fetch 3D vectors:', err);
      setVector3DPoints([]);
    } finally {
      setLoading3D(false);
    }
  };

  useEffect(() => {
    dispatch(fetchVectorSpaceStats());
  }, [dispatch]);

  useEffect(() => {
    fetchVector3D();
  }, []);

  const filteredVector3DPoints = selectedUserId
    ? vector3DPoints.filter(p => p.userId === selectedUserId)
    : vector3DPoints;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
      <Section title="Vector Space Overview" onRefresh={handleRefresh} refreshing={loading || loading3D}>
        <Card sx={{ mb: 2.5, backgroundColor: 'background.paper' }}>
          <CardContent>
            <Typography variant="body2" color="textPrimary" sx={{ lineHeight: 1.6 }}>
              <strong>What is this?</strong> The vector space is a high-dimensional representation of all memories,
              knowledge, and context stored in the system. Each point represents an embedded piece of information.
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.6, mt: 1 }}>
              <strong>Purpose:</strong> Visualize and understand what VictorIA knows and remembers.
              The 3D view shows how different topics and concepts relate to each other in semantic space.
            </Typography>
          </CardContent>
        </Card>

        {error && <GlowAlert sx={{ mb: 2.5 }}>{error}</GlowAlert>}
        {loading && <GlowAlert severity="info">Loading vector space stats...</GlowAlert>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '20px' }}>
          <div>
            <Typography variant="body2" color="textSecondary">Total Vectors</Typography>
            <Typography variant="h5">{stats?.total_vectors || 0}</Typography>
          </div>
          <div>
            <Typography variant="body2" color="textSecondary">Collections</Typography>
            <Typography variant="h5">{stats?.collections_count || 0}</Typography>
          </div>
          <div>
            <Typography variant="body2" color="textSecondary">Memory References</Typography>
            <Typography variant="h5">{stats?.memory_refs || 0}</Typography>
          </div>
          <div>
            <Typography variant="body2" color="textSecondary">Knowledge Chunks</Typography>
            <Typography variant="h5">{stats?.knowledge_chunks || 0}</Typography>
          </div>
        </div>

        {loading3D && <GlowAlert severity="info">Loading 3D vector space...</GlowAlert>}
      </Section>

      <Box sx={{ flex: 1, overflow: 'hidden', mb: 2, display: 'flex', gap: 2, minHeight: 0 }}>
        <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0, position: 'relative' }}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'transparent', boxShadow: 'none' }}>
            <CardContent sx={{ flex: 1, overflow: 'hidden', p: 0, display: 'flex', backgroundColor: 'transparent' }}>
              <VectorSpace3D
                ref={vectorSpace3DRef}
                points={filteredVector3DPoints}
                theme={theme}
                visibleTopics={visibleTopics}
                showLinks={showLinks}
              />
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ width: 280, overflow: 'hidden' }}>
          <VectorSpaceControls
            visibleTopics={visibleTopics}
            onToggleTopic={handleToggleTopic}
            onRefresh={handleRefresh}
            onResetCamera={handleResetCamera}
            onCenterView={handleCenterView}
            vectorCount={filteredVector3DPoints.length}
            visibleCount={filteredVector3DPoints.filter(p => visibleTopics.has(p.topic)).length}
            points={filteredVector3DPoints}
            users={users}
            selectedUserId={selectedUserId}
            onUserChange={handleUserChange}
            showLinks={showLinks}
            onToggleLinks={setShowLinks}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default VectorSpacePage;
